/**
 * Header values (handoff sections 8 and 9): the page toolbar and the project trail row. Copied from
 * the handoff, not derived from the theme, so a change to the theme's type roles cannot move them.
 */
export const HEADER = {
  surface: 'linear-gradient(135deg,#fff 0%,#fafbfd 100%)',
  border: '1px solid #ededed',
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.06)',
  minHeight: 52,
  ink: '#1a1a1a',
  body: '#404040',
  muted: '#6b6b6b',
  link: '#0067b8',
  linkDark: '#004080',
  divider: '#d6d6d6',
  title: { fontSize: 20, fontWeight: 600, letterSpacing: '-0.2px', lineHeight: 1.25 },
  subtitle: { fontSize: 13, fontWeight: 400, lineHeight: 1.5 },
  action: {
    height: 30,
    padding: '0 14px',
    radius: '8px',
    fontSize: 13,
    fontWeight: 600,
    primaryBackground: 'linear-gradient(135deg,#0067b8 0%,#004080 100%)',
    primaryShadow: '0 1px 3px rgba(0,64,128,0.3)',
  },
  // The project trail row: a wordmark group that hangs on the left, and the trail beside it.
  project: {
    trailPaddingLeft: 122,
    trailMarginBottom: 28,
    wordmarkLeft: -2,
    wordmarkTop: -6,
    tile: 30,
    tileMark: 22,
    tileRadius: '8px',
    tileBackground: 'linear-gradient(135deg,#0067b8,#004080)',
    tileShadow: '0 2px 8px rgba(0,103,184,0.3)',
    dividerColor: '#d4d4d4',
  },
} as const;

type Widen<T> = T extends string ? string : T extends number ? number : { -readonly [K in keyof T]: Widen<T[K]> };
export type HeaderTokens = Widen<typeof HEADER>;

/**
 * The handoff specifies the header in light only. In dark mode its white strip, #1a1a1a ink and #ededed
 * hairline read as a bright bar over a dark page, and the dark ink vanishes on the dark trail row. These
 * are the dark counterparts, taken from the dark palette (`colorsDark`): the card surface for the strip,
 * light greys for ink and body, and `primary.light` for links so they clear 4.5:1 on it.
 */
const HEADER_DARK: HeaderTokens = {
  ...HEADER,
  surface: 'linear-gradient(135deg,#2d2d2d 0%,#262626 100%)',
  border: '1px solid #404040',
  shadow: '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
  ink: '#ffffff',
  body: '#a3a3a3',
  muted: '#a3a3a3',
  link: '#66b3ff',
  divider: '#525252',
  project: { ...HEADER.project, dividerColor: '#525252' },
};

/** The header tokens for the active theme. Light is the handoff's own literals. */
export function headerFor(theme: { palette: { mode: 'light' | 'dark' } }): HeaderTokens {
  return theme.palette.mode === 'dark' ? HEADER_DARK : HEADER;
}
